/**
 * Deep-dive inventory report panels — Reorder, Aging, Valuation,
 * COGS-vs-Revenue, ABC analysis.
 *
 * Each panel is a self-contained component receiving { category, from, to,
 * authHeader }. They fetch their own data so tabs can be lazy-loaded.
 *
 * Used by the tabbed `InventoryReport` page.
 */
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Loader2, ArrowDownToLine, AlertTriangle, CheckCircle2, Clock, IndianRupee, Boxes as BoxesIcon, Layers, TrendingDown } from 'lucide-react';
import { API } from '../../../lib/api';
import { fmtINR, formatErr } from '../../../components/admin/billing/shared';
import { StatCard, SourceToggle } from './_shared';

// ─────────────────────────────────────────────────────────────────────────────
// REORDER SUGGESTION
// ─────────────────────────────────────────────────────────────────────────────
export const ReorderPanel = ({ category, authHeader }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [coverDays, setCoverDays] = useState(30);
  const [windowDays, setWindowDays] = useState(90);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      params.set('days_window', windowDays);
      params.set('cover_target_days', coverDays);
      const { data: d } = await axios.get(`${API}/reports/inventory/reorder?${params}`, { headers: authHeader() });
      setData(d);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, category, coverDays, windowDays]);

  useEffect(() => { load(); }, [load]);

  const urgencyClass = (u) => u === 'critical' ? 'bg-red-100 text-red-700 border-red-300' :
    u === 'warning' ? 'bg-amber-100 text-amber-700 border-amber-300' : 'bg-emerald-100 text-emerald-700 border-emerald-300';

  return (
    <div className="space-y-4" data-testid="reorder-panel">
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap gap-3 items-end text-xs">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Velocity window (days)</label>
          <select value={windowDays} onChange={(e) => setWindowDays(Number(e.target.value))} className="border border-slate-300 rounded px-2 py-1 text-xs">
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Target days of cover</label>
          <select value={coverDays} onChange={(e) => setCoverDays(Number(e.target.value))} className="border border-slate-300 rounded px-2 py-1 text-xs">
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={45}>45 days</option>
            <option value={60}>60 days</option>
          </select>
        </div>
        <p className="text-xs text-slate-500 ml-auto max-w-md">Suggests reorder qty so each SKU lasts the target days. Velocity uses the last window.</p>
      </div>

      {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Critical" value={data?.summary?.critical ?? 0} color="text-red-700" testid="reorder-critical" hint="At or below safety, or ≤7 days cover" />
        <StatCard label="Warning" value={data?.summary?.warning ?? 0} color="text-amber-700" testid="reorder-warning" hint="≤14 days cover or suggested qty >0" />
        <StatCard label="OK" value={data?.summary?.ok ?? 0} color="text-emerald-700" testid="reorder-ok" />
        <StatCard label="Total SKUs" value={data?.summary?.total ?? 0} testid="reorder-total" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
          <h2 className="text-sm font-semibold text-slate-700">Suggested reorders · sorted by urgency</h2>
        </div>
        {loading ? (
          <div className="p-6 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50/70 sticky top-0 text-left border-b border-slate-100">
                <tr>
                  <th className="px-3 py-2 font-semibold text-slate-600">Status</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Item</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Cat</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">On hand</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Safety</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Avg/day</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Cover</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Suggested PO</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data?.rows || []).map((r) => (
                  <tr key={r.item_id} className="hover:bg-slate-50" data-testid={`reorder-row-${r.item_id}`}>
                    <td className="px-3 py-2"><span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${urgencyClass(r.urgency)}`}>{r.urgency}</span></td>
                    <td className="px-3 py-2 font-medium text-slate-800">{r.name}</td>
                    <td className="px-3 py-2 text-slate-600 uppercase text-[10px] tracking-wider">{r.category}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.current_qty} {r.unit}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-400">{r.safety_stock}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-600">{r.avg_per_day || '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.days_of_cover === null ? '∞' : `${r.days_of_cover}d`}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-teal-700">{r.suggested_reorder > 0 ? `${r.suggested_reorder} ${r.unit}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// AGING / DEAD STOCK
// ─────────────────────────────────────────────────────────────────────────────
export const AgingPanel = ({ category, authHeader }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [activeBucket, setActiveBucket] = useState('90');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      const { data: d } = await axios.get(`${API}/reports/inventory/aging?${params}`, { headers: authHeader() });
      setData(d);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, category]);

  useEffect(() => { load(); }, [load]);

  const tabs = useMemo(() => [
    { key: '90', label: '90+ days idle', color: 'red' },
    { key: '60', label: '60–89 days', color: 'amber' },
    { key: '30', label: '30–59 days', color: 'yellow' },
    { key: 'never', label: 'Never consumed', color: 'slate' },
  ], []);

  const bucket = data?.buckets?.[activeBucket];

  return (
    <div className="space-y-4" data-testid="aging-panel">
      {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {tabs.map((t) => {
          const b = data?.buckets?.[t.key];
          return (
            <button
              key={t.key}
              onClick={() => setActiveBucket(t.key)}
              className={`text-left p-3 rounded-xl border transition ${activeBucket === t.key ? `bg-${t.color}-50 border-${t.color}-300 shadow-sm` : 'bg-white border-slate-200 hover:bg-slate-50'}`}
              data-testid={`aging-tab-${t.key}`}
            >
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{t.label}</div>
              <div className="text-xl font-bold text-slate-900">{b?.count ?? 0}</div>
              {(b?.value || 0) > 0 && (
                <div className="text-[10px] text-slate-500 mt-0.5">≈ {fmtINR(b.value)} tied up</div>
              )}
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">{tabs.find((t) => t.key === activeBucket)?.label} · {bucket?.count ?? 0} items</h2>
          {(bucket?.value || 0) > 0 && <span className="text-xs text-slate-500">Total value: <b className="text-slate-700">{fmtINR(bucket.value)}</b></span>}
        </div>
        {loading ? (
          <div className="p-6 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading…</div>
        ) : (bucket?.items || []).length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
            <CheckCircle2 size={24} className="text-emerald-500" />
            Nothing in this bucket — your inventory is moving briskly!
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[520px]">
            <table className="w-full text-xs">
              <thead className="bg-slate-50/70 sticky top-0 text-left border-b border-slate-100">
                <tr>
                  <th className="px-3 py-2 font-semibold text-slate-600">Item</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">SKU</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Cat</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Qty</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Unit cost</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Value</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Days idle</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {bucket.items.map((i) => (
                  <tr key={i.item_id} className="hover:bg-slate-50" data-testid={`aging-row-${i.item_id}`}>
                    <td className="px-3 py-2 text-slate-800">{i.name}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{i.sku || '—'}</td>
                    <td className="px-3 py-2 text-slate-600 uppercase text-[10px] tracking-wider">{i.category}</td>
                    <td className="px-3 py-2 text-right font-mono">{i.current_qty} {i.unit}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-500">{i.unit_cost != null ? fmtINR(i.unit_cost) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-slate-700">{i.value != null ? fmtINR(i.value) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-amber-700">{i.days_idle ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// STOCK VALUATION
// ─────────────────────────────────────────────────────────────────────────────
export const ValuationPanel = ({ category, authHeader }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      const { data: d } = await axios.get(`${API}/reports/inventory/valuation?${params}`, { headers: authHeader() });
      setData(d);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, category]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="valuation-panel">
      {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatCard label="Total stock value" value={fmtINR(data?.total_value ?? 0)} color="text-emerald-700" testid="val-total" />
        <StatCard label="Priced items value" value={fmtINR(data?.priced_value ?? 0)} testid="val-priced" />
        <StatCard label="Unpriced items" value={data?.unpriced_items ?? 0} color={data?.unpriced_items ? 'text-amber-700' : 'text-emerald-700'} testid="val-unpriced" hint={data?.unpriced_items ? 'Set unit cost on item edit screen' : ''} />
      </div>

      {data?.unpriced_items > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3 text-sm flex items-start gap-2" data-testid="val-unpriced-warning">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <span>
            <b>{data.unpriced_items}</b> item{data.unpriced_items > 1 ? 's are' : ' is'} missing unit cost. Their qty is excluded from valuation.
            Set the cost by editing the item, or capture it on the next restock.
          </span>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
          <h2 className="text-sm font-semibold text-slate-700">By category</h2>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-slate-50/70 text-left border-b border-slate-100">
            <tr>
              <th className="px-3 py-2 font-semibold text-slate-600">Category</th>
              <th className="px-3 py-2 font-semibold text-slate-600 text-right">Items</th>
              <th className="px-3 py-2 font-semibold text-slate-600 text-right">Total qty</th>
              <th className="px-3 py-2 font-semibold text-slate-600 text-right">Value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data?.by_category || []).map((c) => (
              <tr key={c.category} className="hover:bg-slate-50" data-testid={`val-cat-${c.category}`}>
                <td className="px-3 py-2 text-slate-800 uppercase font-semibold tracking-wider">{c.category}</td>
                <td className="px-3 py-2 text-right font-mono">{c.items}</td>
                <td className="px-3 py-2 text-right font-mono text-slate-600">{c.qty}</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">{fmtINR(c.value)}</td>
              </tr>
            ))}
            {(data?.by_category || []).length === 0 && !loading && (
              <tr><td colSpan="4" className="p-6 text-center text-slate-400 text-sm">No priced inventory yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
          <h2 className="text-sm font-semibold text-slate-700">All items · sorted by value</h2>
        </div>
        {loading ? (
          <div className="p-6 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading…</div>
        ) : (
          <div className="overflow-x-auto max-h-[520px]">
            <table className="w-full text-xs">
              <thead className="bg-slate-50/70 sticky top-0 text-left border-b border-slate-100">
                <tr>
                  <th className="px-3 py-2 font-semibold text-slate-600">Item</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">SKU</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Cat</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Qty</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Unit cost</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data?.rows || []).map((r) => (
                  <tr key={r.item_id} className={`hover:bg-slate-50 ${r.unpriced ? 'opacity-60' : ''}`} data-testid={`val-row-${r.item_id}`}>
                    <td className="px-3 py-2 text-slate-800">{r.name}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{r.sku || '—'}</td>
                    <td className="px-3 py-2 text-slate-600 uppercase text-[10px] tracking-wider">{r.category}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.qty}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-500">
                      {r.unit_cost != null ? fmtINR(r.unit_cost) : <span className="text-amber-700 italic">unpriced</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700">{r.unpriced ? '—' : fmtINR(r.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// COGS vs REVENUE
// ─────────────────────────────────────────────────────────────────────────────
export const CogsPanel = ({ from, to, authHeader }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState('gst');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = new URLSearchParams();
      if (from) params.set('date_from', from);
      if (to) params.set('date_to', to);
      params.set('source', source);
      const { data: d } = await axios.get(`${API}/reports/inventory/cogs-vs-revenue?${params}`, { headers: authHeader() });
      setData(d);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, from, to, source]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="cogs-panel">
      {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err}</div>}

      <SourceToggle value={source} onChange={setSource} idPrefix="cogs-source" />

      <p className="text-xs text-slate-500 max-w-3xl">
        Revenue is matched to material category using line-item keywords (PPF / sunfilm-film / bodyshop-paint-denting).
        COGS uses material consumed × per-unit cost (from your restock entries). Margin per category lets you spot money-losers fast.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total revenue" value={fmtINR(data?.totals?.revenue ?? 0)} color="text-emerald-700" testid="cogs-revenue" />
        <StatCard label="Total COGS" value={fmtINR(data?.totals?.cogs ?? 0)} color="text-rose-700" testid="cogs-cogs" />
        <StatCard label="Margin" value={fmtINR(data?.totals?.margin ?? 0)} color={(data?.totals?.margin ?? 0) >= 0 ? 'text-emerald-700' : 'text-red-700'} testid="cogs-margin" />
        <StatCard label="Margin %" value={data?.totals?.margin_pct != null ? `${data.totals.margin_pct}%` : '—'} testid="cogs-margin-pct" />
      </div>

      {data?.unpriced_consume_entries > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3 text-sm flex items-start gap-2">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <span>
            <b>{data.unpriced_consume_entries}</b> consume entries had no captured unit cost — COGS is conservative (under-counted).
            Capture unit cost on future restocks for accuracy.
          </span>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
          <h2 className="text-sm font-semibold text-slate-700">Per-category margin</h2>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-slate-50/70 text-left border-b border-slate-100">
            <tr>
              <th className="px-3 py-2 font-semibold text-slate-600">Category</th>
              <th className="px-3 py-2 font-semibold text-slate-600 text-right">Units consumed</th>
              <th className="px-3 py-2 font-semibold text-slate-600 text-right">COGS</th>
              <th className="px-3 py-2 font-semibold text-slate-600 text-right">Revenue</th>
              <th className="px-3 py-2 font-semibold text-slate-600 text-right">Margin</th>
              <th className="px-3 py-2 font-semibold text-slate-600 text-right">Margin %</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data?.rows || []).map((r) => (
              <tr key={r.category} className="hover:bg-slate-50" data-testid={`cogs-row-${r.category}`}>
                <td className="px-3 py-2 text-slate-800 uppercase font-semibold tracking-wider">{r.category}</td>
                <td className="px-3 py-2 text-right font-mono">{r.units_consumed}</td>
                <td className="px-3 py-2 text-right font-mono text-rose-700">{fmtINR(r.cogs)}</td>
                <td className="px-3 py-2 text-right font-mono text-emerald-700">{fmtINR(r.revenue)}</td>
                <td className={`px-3 py-2 text-right font-mono font-bold ${r.margin >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmtINR(r.margin)}</td>
                <td className="px-3 py-2 text-right font-mono">{r.margin_pct != null ? `${r.margin_pct}%` : '—'}</td>
              </tr>
            ))}
          </tbody>
          {(data?.other_revenue || 0) > 0 && (
            <tfoot className="text-[11px] text-slate-500 border-t border-slate-100">
              <tr>
                <td colSpan="6" className="px-3 py-2 italic">
                  Uncategorized revenue (consultation/labour/etc.): <b className="text-slate-700">{fmtINR(data.other_revenue)}</b>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ABC ANALYSIS
// ─────────────────────────────────────────────────────────────────────────────
export const AbcPanel = ({ category, authHeader }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [windowDays, setWindowDays] = useState(90);
  const [activeClass, setActiveClass] = useState('A');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      params.set('days_window', windowDays);
      const { data: d } = await axios.get(`${API}/reports/inventory/abc?${params}`, { headers: authHeader() });
      setData(d);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, category, windowDays]);

  useEffect(() => { load(); }, [load]);

  const classCfg = {
    A: { color: 'emerald', label: 'A · Top 80% of value', desc: 'Most critical — never let these stock out.' },
    B: { color: 'amber',  label: 'B · Next 15%',         desc: 'Steady movers — standard reorder cadence.' },
    C: { color: 'slate',  label: 'C · Trailing 5%',      desc: 'Slow / rare. Consider lower safety stock.' },
  };

  const classRows = (data?.rows || []).filter((r) => r.class === activeClass);

  return (
    <div className="space-y-4" data-testid="abc-panel">
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap gap-3 items-end text-xs">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Window</label>
          <select value={windowDays} onChange={(e) => setWindowDays(Number(e.target.value))} className="border border-slate-300 rounded px-2 py-1 text-xs">
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
            <option value={365}>Last 365 days</option>
          </select>
        </div>
        <p className="text-xs text-slate-500 ml-auto max-w-md">
          ABC classifies items by consumption value (qty × unit cost). Class A = top 80% of total value.
        </p>
      </div>

      {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err}</div>}

      <div className="grid grid-cols-3 gap-3">
        {['A', 'B', 'C'].map((cls) => {
          const cfg = classCfg[cls];
          const sum = data?.class_summary?.[cls];
          return (
            <button
              key={cls}
              onClick={() => setActiveClass(cls)}
              className={`text-left p-3 rounded-xl border transition ${activeClass === cls ? `bg-${cfg.color}-50 border-${cfg.color}-300 shadow-sm` : 'bg-white border-slate-200 hover:bg-slate-50'}`}
              data-testid={`abc-tab-${cls}`}
            >
              <div className={`text-[10px] uppercase tracking-wider font-bold text-${cfg.color}-700 mb-1`}>{cfg.label}</div>
              <div className="text-xl font-bold text-slate-900">{sum?.count ?? 0} <span className="text-xs font-normal text-slate-500">items</span></div>
              <div className="text-[10px] text-slate-500 mt-0.5">{fmtINR(sum?.value ?? 0)} value</div>
              <div className="text-[10px] text-slate-400 mt-1 italic">{cfg.desc}</div>
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
          <h2 className="text-sm font-semibold text-slate-700">Class {activeClass} items</h2>
        </div>
        {loading ? (
          <div className="p-6 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading…</div>
        ) : classRows.length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-sm">No consumption recorded in this window for class {activeClass}.</div>
        ) : (
          <div className="overflow-x-auto max-h-[520px]">
            <table className="w-full text-xs">
              <thead className="bg-slate-50/70 sticky top-0 text-left border-b border-slate-100">
                <tr>
                  <th className="px-3 py-2 font-semibold text-slate-600">Item</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Cat</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Qty consumed</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Value</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">% of total</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Jobs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {classRows.map((r) => (
                  <tr key={r.item_id} className="hover:bg-slate-50" data-testid={`abc-row-${r.item_id}`}>
                    <td className="px-3 py-2 text-slate-800">{r.name}</td>
                    <td className="px-3 py-2 text-slate-600 uppercase text-[10px] tracking-wider">{r.category}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.consumed_qty} {r.unit}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-slate-700">{fmtINR(r.consumed_value)}</td>
                    <td className="px-3 py-2 text-right font-mono text-teal-700">{r.value_pct}%</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-500">{r.entries}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Re-export icons for the tabbed wrapper to use
export const ICONS = { ArrowDownToLine, Clock, IndianRupee, BoxesIcon, Layers, TrendingDown };
